/**
 * One device, in full.
 *
 * The page an operator lands on when something is wrong, which sets the rule it
 * has to keep: it must render for a device that is offline, unclaimed, lapsed, or
 * all three. Every decision about what that looks like lives in deviceDetail.ts
 * and is tested there; this file renders the answer.
 *
 * A failed refresh keeps whatever was last known and adds a marker. It never
 * replaces the page with an error — "this camera was streaming eleven minutes
 * ago" is actionable and "Error" is not.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { AddCamera } from './AddCamera.tsx';
import { ApiError, fetchDevice, reissueClaimCode, reprovisionDevice } from './api.ts';
import { CameraRow } from './CameraRow.tsx';
import { deviceDetailViewModel, type DeviceDetailData } from './deviceDetail.ts';
import { DeleteDevice } from './DeleteDevice.tsx';
import { EditEntitlement } from './EditEntitlement.tsx';
import type { FetchState } from './deviceViewModel.ts';
import type { IssuedClaim } from './DeviceList.tsx';
import { routeHref } from './routing.ts';
import { ScanForCameras } from './ScanForCameras.tsx';

export function DeviceDetail({
  deviceId,
  now,
  onChanged,
  onClaimIssued,
}: {
  deviceId: string;
  now: number;
  onChanged: () => void;
  onClaimIssued: (claim: IssuedClaim) => void;
}): React.JSX.Element {
  const [data, setData] = useState<DeviceDetailData | null>(null);
  const [fetchState, setFetchState] = useState<FetchState>({ status: 'loading' });
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastSucceededAt = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const device = await fetchDevice(deviceId);
      lastSucceededAt.current = Date.now();
      setData(device);
      setMissing(false);
      setFetchState({ status: 'ok', fetchedAt: lastSucceededAt.current });
    } catch (err) {
      /*
       * 404 is the one failure that genuinely replaces the page: the device was
       * deleted, or the link is wrong, and there is nothing stale worth showing.
       * Everything else downgrades confidence and keeps the data.
       */
      if (err instanceof ApiError && err.status === 404) {
        setMissing(true);
        return;
      }
      setFetchState({
        status: 'failed',
        fetchedAt: Date.now(),
        lastSucceededAt: lastSucceededAt.current,
      });
    }
  }, [deviceId]);

  useEffect(() => {
    void load();
    // Same cadence as the fleet list, and paused with the tab for the same reason.
    const timer = window.setInterval(() => {
      if (!document.hidden) void load();
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const refresh = useCallback((): void => {
    void load();
    onChanged();
  }, [load, onChanged]);

  async function generateCode(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await reissueClaimCode(deviceId);
      onClaimIssued({ ...result, deviceName: data?.name ?? 'this device' });
      refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not generate a claim code.');
    } finally {
      setBusy(false);
    }
  }

  async function reprovision(): Promise<void> {
    const confirmed = window.confirm(
      `Re-provision ${data?.name ?? 'this device'}?\n\n` +
        'This revokes its access immediately and disconnects it. The device will ' +
        'stop reaching the cloud until someone claims it again with the new code.\n\n' +
        'Recording on the device itself is unaffected.',
    );
    if (!confirmed) return;

    setBusy(true);
    setError(null);
    try {
      const result = await reprovisionDevice(deviceId, 'operator_revoked');
      onClaimIssued({ ...result, deviceName: data?.name ?? 'this device' });
      refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not re-provision the device.');
    } finally {
      setBusy(false);
    }
  }

  if (missing) {
    return (
      <section className="section">
        <div className="empty">
          <p className="empty__title">This device no longer exists</p>
          <p>It may have been deleted. <a href={routeHref('devices')}>Back to devices</a>.</p>
        </div>
      </section>
    );
  }

  if (!data) {
    // Genuinely nothing to show yet. Distinct from a failed refresh, which keeps
    // whatever was last known.
    return (
      <section className="section">
        <p className="empty">
          {fetchState.status === 'failed' ? 'Cannot reach the server. Retrying.' : 'Loading…'}
        </p>
      </section>
    );
  }

  const model = deviceDetailViewModel(data, fetchState, now);

  return (
    <section className="section detail">
      <p className="detail__back">
        <a href={routeHref('devices')}>← All devices</a>
      </p>

      {/* A marker beside the data, never in place of it. */}
      {model.stale && model.staleReason ? (
        <p className="notice notice--warn" role="status">
          {model.staleReason}
        </p>
      ) : null}

      <div className="card">
        <div className="card__body">
          <div className="detail__head">
            <div>
              <h2 className="detail__name">{model.name}</h2>
              <p className="detail__where">
                {model.clientName ? `${model.clientName} · ` : ''}
                {model.siteName ?? 'No site'}
              </p>
            </div>
            <span className={`status status--${model.health}`}>
              <span className="status__dot" />
              {model.healthLabel}
            </span>
          </div>

          <dl className="facts">
            <div className="fact">
              <dt>Last seen</dt>
              <dd>{model.lastSeenLabel}</dd>
            </div>
            <div className="fact">
              <dt>Firmware</dt>
              <dd>{model.firmwareLabel}</dd>
            </div>
            <div className="fact">
              <dt>Cameras</dt>
              <dd>
                {model.entitlement.max === null
                  ? `${model.entitlement.used}`
                  : `${model.entitlement.used} of ${model.entitlement.max} licensed`}
              </dd>
            </div>
            <div className="fact">
              <dt>Entitlement</dt>
              <dd className={model.entitlement.state === 'expired' ? 'fact--warn' : undefined}>
                {model.entitlement.label}
              </dd>
            </div>
            <div className="fact">
              <dt>Cloud retention</dt>
              <dd>{model.entitlement.retentionLabel}</dd>
            </div>
            <div className="fact">
              <dt>Configuration</dt>
              <dd className={model.config.state === 'pending' ? 'fact--warn' : undefined}>
                {model.config.label}
              </dd>
            </div>
            <div className="fact">
              <dt>Encryption key</dt>
              <dd>
                {model.keyLabel}
                {model.keyFingerprintShort ? (
                  <code className="fingerprint">{model.keyFingerprintShort}</code>
                ) : null}
              </dd>
            </div>
          </dl>

          {error ? (
            <p className="notice notice--error" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <div className="card__foot">
          <EditEntitlement
            deviceId={deviceId}
            maxCameras={data.max_cameras}
            retentionDays={data.cloud_event_retention_days}
            expiresAt={data.entitlement_expires_at}
            camerasInUse={model.entitlement.used}
            onChanged={refresh}
          />

          <DeleteDevice
            deviceId={deviceId}
            deviceName={model.name}
            onDeleted={() => {
              window.location.hash = routeHref('devices');
              onChanged();
            }}
          />

          {model.claimed ? (
            <button
              type="button"
              className="btn btn--sm btn--danger"
              disabled={busy}
              onClick={() => void reprovision()}
            >
              {busy ? 'Working…' : 'Re-provision'}
            </button>
          ) : (
            <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void generateCode()}>
              {busy ? 'Working…' : 'Generate claim code'}
            </button>
          )}
        </div>
      </div>

      <div className="detail__cameras">
        <div className="detail__cameras-head">
          <h3>Cameras</h3>
          {model.entitlement.canAddCamera ? (
            <div className="detail__cameras-actions">
              {/* Scanning first: it is the path that should be taken, and the
                  manual form is the fallback for a camera that will not answer
                  discovery — which many will not, since most ship with it off. */}
              <ScanForCameras deviceId={deviceId} onAdded={refresh} />
              <AddCamera deviceId={deviceId} onAdded={refresh} />
            </div>
          ) : null}
        </div>

        {/* Shown whether or not there are cameras: "why can't I add one" is the
            question this answers, and it is asked most loudly when the list is
            empty. */}
        {model.entitlement.blockedReason ? (
          <p className="notice notice--warn">{model.entitlement.blockedReason}</p>
        ) : null}

        {model.cameras.length === 0 ? (
          <div className="empty">
            <p className="empty__title">No cameras yet</p>
            <p>Add one once the device is on the same network as the camera.</p>
          </div>
        ) : (
          <ul className="camera-rows">
            {model.cameras.map((camera) => (
              <CameraRow key={camera.id} deviceId={deviceId} camera={camera} onChanged={refresh} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
